import type { ContentInstancePosition, ModuleInstanceKey } from "../types/ids";
import type { ViewModuleRef } from "../view/types";
import {
  contentPosition,
  isValidSlotName,
  moduleInstanceKey,
  slotPosition,
} from "./instance-key";
import { isPropsRecord } from "./module";

export function isModuleRef(value: unknown): value is ViewModuleRef {
  return (
    typeof value === "object" &&
    value !== null &&
    "_tag" in value &&
    Reflect.get(value, "_tag") === "ModuleRef"
  );
}

/**
 * The fields the walk reads. `slots` is `unknown` so a view's refs fit
 * whatever context types their prop callbacks take.
 */
export type SlotParentRef = Pick<ViewModuleRef, "moduleId"> & {
  slots?: unknown;
};

/**
 * The child refs a branch ref declares in `module()`'s `slots` option, in
 * declaration order; an omitted (`undefined`) optional slot is skipped. Every
 * consumer — server, manifest builder, prewarm, test harness and client —
 * reads slots through here, so they all see the same tree.
 *
 * @throws when `slots` isn't a plain object, a slot name can't appear in an
 * instance key, or a slot value isn't a module ref, so a malformed view fails
 * its manifest build rather than rendering a slot the server can't see.
 */
export function slotRefs(
  ref: SlotParentRef,
  position: ContentInstancePosition,
): Array<[slotName: string, slotRef: ViewModuleRef]> {
  const { slots } = ref;
  if (slots === undefined) {
    return [];
  }
  const where = `of module "${ref.moduleId}" at position ${position}`;
  if (!isPropsRecord(slots)) {
    throw new Error(
      `Slots ${where} must be a plain object of module refs, one per slot.`,
    );
  }

  const refs: Array<[string, ViewModuleRef]> = [];
  for (const [slotName, slotRef] of Object.entries(slots)) {
    if (!isValidSlotName(slotName)) {
      throw new Error(
        `Slot "${slotName}" ${where} can't be keyed: slot names may only contain letters, digits, "_" and "-".`,
      );
    }
    if (slotRef === undefined) {
      continue;
    }
    if (!isModuleRef(slotRef)) {
      throw new Error(
        `Slot "${slotName}" ${where} must hold a module ref built with module().`,
      );
    }
    refs.push([slotName, slotRef]);
  }
  return refs;
}

/** A view content ref — top-level or in a branch slot — and its position. */
export interface ContentModuleInstance<
  TRef extends SlotParentRef = ViewModuleRef,
> {
  key: ModuleInstanceKey;
  position: ContentInstancePosition;
  ref: TRef | ViewModuleRef;
}

/**
 * A view's content refs and the slot refs declared under them, in render
 * order (each ref before its slots, depth first). The server keys module data
 * and actions by these instances, the manifest builder pins them, and the
 * client keys the same positions while rendering.
 *
 * @throws on a malformed slot (see {@link slotRefs}).
 */
export function collectContentModuleInstances<TRef extends SlotParentRef>(
  contentRefs: ReadonlyArray<TRef>,
): Array<ContentModuleInstance<TRef>> {
  const instances: Array<ContentModuleInstance<TRef>> = [];

  const visit = (
    ref: TRef | ViewModuleRef,
    position: ContentInstancePosition,
  ) => {
    instances.push({
      key: moduleInstanceKey(position, ref.moduleId),
      position,
      ref,
    });
    for (const [slotName, slotRef] of slotRefs(ref, position)) {
      visit(slotRef, slotPosition(position, slotName));
    }
  };

  contentRefs.forEach((ref, index) => visit(ref, contentPosition(index)));
  return instances;
}
